import {RUN_SELECTOR_CHILDREN_PAGE_SIZE} from '../../util/constants';
import {type Grouping} from '../../util/queryTypes';
import type * as Run from '../../util/runs';
import {
  getSelectionPath,
  type SelectionPath,
  type SelectionRangeRow,
} from '../../util/selectionmanager';

/**
 * a run as the run selector table holds it. __address__ is added client side
 * and keys getChildRows lookups
 */
export type RangeRow = Run.RunWithSupportedKeys & {__address__: string};

/** one rendered list (the top level, or an expanded group's children), reused at each depth */
type RangeLevel = {
  getChildRows(address: string): readonly RangeRow[] | null;
  grouping: Grouping;
  pageSize: number;
  /** rows shown above the page, like pinned runs at the top level */
  pinnedRows: readonly RangeRow[];
  /** every loaded row, of which the table shows one page */
  rows: readonly RangeRow[];
};

type GetRangeRowsParams = RangeLevel & {
  anchorPath: SelectionPath;
  targetPath: SelectionPath;
};

/**
 * the rows a shift+click covers, from anchor to target in table order, or null
 * when either endpoint can't be found.
 *
 * - each list is read from the page holding an endpoint, so both endpoints
 *   must be on screen
 * - only groups holding an endpoint are opened. selectRange sets a group
 *   between the endpoints as a whole, and skips the opened groups' headers
 */
export function getRangeRows({
  anchorPath,
  targetPath,
  ...level
}: GetRangeRowsParams): SelectionRangeRow[] | null {
  const flatRows = flattenRows(level, 1, [anchorPath, targetPath]);
  if (flatRows == null) {
    return null;
  }

  const findRowIndex = (path: SelectionPath) =>
    flatRows.findIndex(({depth, run}) => {
      const rowPath = getSelectionPath(level.grouping, run, depth);
      return isSamePath(rowPath, path);
    });

  const anchorIndex = findRowIndex(anchorPath);
  const targetIndex = findRowIndex(targetPath);
  if (anchorIndex === -1 || targetIndex === -1) {
    return null;
  }

  if (anchorIndex <= targetIndex) {
    return flatRows.slice(anchorIndex, targetIndex + 1);
  }
  return flatRows.slice(targetIndex, anchorIndex + 1).reverse();
}

function getVisibleRows(
  level: RangeLevel,
  depth: number,
  endpointPaths: readonly SelectionPath[]
): RangeRow[] {
  const endpointIndex = level.rows.findIndex(row =>
    endpointPaths.some(path => {
      const rowPath = getSelectionPath(level.grouping, row, depth);
      return isOnBranch(rowPath, path);
    })
  );
  if (endpointIndex === -1) {
    return [...level.pinnedRows];
  }

  // the endpoint is on screen, so the page holding it is the page shown
  const pageStart = Math.floor(endpointIndex / level.pageSize) * level.pageSize;
  return [
    ...level.pinnedRows,
    ...level.rows.slice(pageStart, pageStart + level.pageSize),
  ];
}

// the rows on screen in table order, opening only the groups that hold an
// endpoint
function flattenRows(
  level: RangeLevel,
  depth: number,
  endpointPaths: readonly SelectionPath[]
): SelectionRangeRow[] | null {
  const flatRows: SelectionRangeRow[] = [];
  for (const row of getVisibleRows(level, depth, endpointPaths)) {
    flatRows.push({depth, run: row});

    const rowPath = getSelectionPath(level.grouping, row, depth);
    const childEndpointPaths = endpointPaths.filter(
      path => path.length > depth && isOnBranch(rowPath, path)
    );
    if (childEndpointPaths.length === 0) {
      continue;
    }

    const childRows = level.getChildRows(row.__address__);
    if (childRows == null) {
      return null;
    }

    const childLevel = {
      ...level,
      pageSize: RUN_SELECTOR_CHILDREN_PAGE_SIZE,
      pinnedRows: [],
      rows: childRows,
    };
    const flatChildRows = flattenRows(
      childLevel,
      depth + 1,
      childEndpointPaths
    );
    if (flatChildRows == null) {
      return null;
    }

    flatRows.push(...flatChildRows);
  }
  return flatRows;
}

// a row's path at its own depth is a prefix of every path under it
function isOnBranch(rowPath: SelectionPath, path: SelectionPath): boolean {
  return (
    rowPath.length <= path.length &&
    rowPath.every((value, i) => value === path[i])
  );
}

function isSamePath(a: SelectionPath, b: SelectionPath): boolean {
  return a.length === b.length && isOnBranch(a, b);
}
